import { Box, Button, Checkbox, Drawer, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, useForm } from "react-hook-form";
import { useState, useContext } from "react";
import pt from "date-fns/locale/pt";
import { parseISO, getDay } from "date-fns";
import { useBulkCreateSchedules } from "../../../../api/moloni/useBulkCreateSchedules";
import { useUpdateInvoiceSchedule } from "../../../../api/moloni/useUpdateInvoiceSchedule";
import { useInvoiceSchedulesByTreatment } from "../../../../api/moloni/useInvoiceSchedulesByTreatment";
import { useStore } from "../../../../store/store";
import { RecordContext } from "../../../../components/record/Record";
import type { InvoiceSchedule, OrderItemForScheduling } from "../types";
import {
    validateScheduledDate,
    validateScheduledTotal,
    calculateItemsTotal,
    showError,
    checkExistingSchedulesInWeek,
} from "./scheduleValidations";
import { calculateBulkDates, DAYS_OF_WEEK, calculateUnitPrice, generateScheduleItemsFromOrderItems, calculateTotalInvoicesCount, toLocalISOString, isSchedulePending } from "./utils";
import { RecentScheduledInvoices } from "./RecentScheduledInvoices";
import { ScheduleConflictDialog } from "./ScheduleConflictDialog";

type ScheduleFormProps = {
    isOpen: boolean;
    orderId: number;
    treatmentId: number;
    onClose: () => void;
    orderTotal: number;
    pendingScheduledTotal: number;
    invoicedTotal: number | null;
    orderItems: Array<OrderItemForScheduling>;
    schedule?: InvoiceSchedule;
};

type ScheduleFormData = {
    first_date: Date | null;
    status: "scheduled" | "created";
    frequency: "único" | "daily" | "weekly" | undefined;
    days_of_week: Array<number>;
};

export const ScheduleForm = ({
    isOpen,
    orderId,
    treatmentId,
    onClose,
    orderTotal,
    pendingScheduledTotal,
    invoicedTotal,
    orderItems,
    schedule,
}: ScheduleFormProps) => {
    const { notifications } = useStore();
    const recordContext = useContext(RecordContext);
    const paymentStatus = recordContext?.item?.payment_status;
    const { mutateAsync: bulkCreateSchedules, isLoading: isBulkCreating } = useBulkCreateSchedules();
    const { mutateAsync: updateSchedule, isLoading: isUpdating } = useUpdateInvoiceSchedule();
    const { data: existingSchedulesInTreatment } = useInvoiceSchedulesByTreatment(treatmentId);

    const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
    const [conflictCount, setConflictCount] = useState(0);
    const [pendingFormData, setPendingFormData] = useState<ScheduleFormData | null>(null);

    const isEditMode = !!schedule;
    const isLoading = isBulkCreating || isUpdating;
    
    const totalInvoicesCount = calculateTotalInvoicesCount(orderItems);
    const generatedItems = generateScheduleItemsFromOrderItems(orderItems);

    const todayWeekDay = getDay(new Date());
    const defaultDaysOfWeek = todayWeekDay === 0 ? [] : [todayWeekDay];

    const form = useForm<ScheduleFormData>({
        defaultValues: {
            first_date: schedule ? parseISO(schedule.scheduled_date) : new Date(),
            status: schedule?.status || "scheduled",
            frequency: "weekly",
            days_of_week: defaultDaysOfWeek,
        },
    });


    const createSchedulesWithData = async (data: ScheduleFormData) => {
        if (!data.frequency) return;
        
        const dates = calculateBulkDates(data.first_date!, totalInvoicesCount, data.frequency, data.days_of_week);
        const newItemsTotal = calculateItemsTotal(generatedItems);

        if (!validateScheduledTotal(invoicedTotal, pendingScheduledTotal, newItemsTotal, orderTotal, notifications, 1))
            return;

        const schedulesToCreate = dates.map((date, index) => ({
            order: orderId,
            scheduled_date: toLocalISOString(date),
            status: "scheduled" as const,
        }));

        await bulkCreateSchedules({
            schedules: schedulesToCreate,
            items: generatedItems.map((item, index) => ({
                ...item,
                scheduleIndex: index,
            })),
        });

        notifications.setNotification({
            message: `${dates.length} agendamento(s) criado(s) com sucesso`,
            severity: "success",
            isOpen: true,
        });

        onClose();
    };

    const handleConfirmConflict = async () => {
        setIsConflictDialogOpen(false);
        if (pendingFormData) {
            try {
                await createSchedulesWithData(pendingFormData);
            } catch (error: any) {
                const backendErrorMessage = error?.response?.data?.errors?.[0]?.message;
                showError(notifications, backendErrorMessage || "Erro ao criar agendamento");
            }
        }
        setPendingFormData(null);
    };

    const handleCancelConflict = () => {
        setIsConflictDialogOpen(false);
        setPendingFormData(null);
    };

    const onSubmit = async (data: ScheduleFormData) => {
        if (paymentStatus !== "paid") {
            return showError(notifications, "Não é possível agendar faturas para compras não pagas");
        }
        
        if (!validateScheduledDate(data.first_date, notifications)) return;
        
        if (!isEditMode && data.days_of_week.length === 0) {
            return showError(notifications, "Por favor, selecione pelo menos um dia da semana");
        }

        try {
            if (isEditMode) {
                const scheduleItemsTotal = schedule.scheduled_invoices_items
                    ? calculateItemsTotal(schedule.scheduled_invoices_items)
                    : 0;
                const adjustedPendingTotal = isSchedulePending(schedule)
                    ? pendingScheduledTotal - scheduleItemsTotal
                    : pendingScheduledTotal;

                if (!validateScheduledTotal(invoicedTotal, adjustedPendingTotal, scheduleItemsTotal, orderTotal, notifications)) return;

                await updateSchedule({
                    id: schedule.id,
                    scheduled_date: toLocalISOString(data.first_date!),
                    status: data.status,
                    items: schedule.scheduled_invoices_items || [],
                });

                notifications.setNotification({
                    message: "Agendamento atualizado com sucesso",
                    severity: "success",
                    isOpen: true,
                });
                
                onClose();
            } else {
                const scheduledOnly = (existingSchedulesInTreatment || []).filter(
                    (s: InvoiceSchedule) => s.status === "scheduled"
                );
                const existingSchedulesCount = checkExistingSchedulesInWeek(data.first_date!, scheduledOnly);
                
                if (existingSchedulesCount >= 2) {
                    setPendingFormData(data);
                    setConflictCount(existingSchedulesCount);
                    setIsConflictDialogOpen(true);
                    return;
                }

                await createSchedulesWithData(data);
            }
        } catch (error: any) {
            const backendErrorMessage = error?.response?.data?.errors?.[0]?.message;
            const defaultErrorMessage = isEditMode ? "Erro ao atualizar agendamento" : "Erro ao criar agendamento";
            showError(notifications, backendErrorMessage || defaultErrorMessage);
        }
    };

    return (
        <Drawer
            anchor="bottom"
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    maxHeight: "90vh",
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    backgroundColor: "#fff",
                    width: "70%",
                    left: "20%",
                    mb: 2
                },
            }}
        >
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={{ p: 6, width: "100%" }}>
                    <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
                        {isEditMode ? "Editar Agendamento de Fatura" : "Adicionar Agendamento de Fatura"}
                    </Typography>

                    {!isEditMode && <RecentScheduledInvoices treatmentId={treatmentId} />}

                    {!isEditMode && (
                        <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Serão criados {totalInvoicesCount} agendamentos com base nos items da compra:
                            </Typography>
                            {orderItems.map((item, index) => (
                                <Typography key={index} variant="body2" sx={{ ml: 2, mt: 1 }}>
                                    • {item.serviceName}: {item.quantity} fatura(s) de €{calculateUnitPrice(item).toFixed(2)}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pt}>
                            <Controller
                                name="first_date"
                                control={form.control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        label={isEditMode ? "Data Agendada" : "Primeira Data"}
                                        format="dd/MM/yyyy"
                                        sx={{ width: "100%" }}
                                        slotProps={{
                                            textField: {
                                                required: true,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </LocalizationProvider>

                        {!isEditMode && (
                            <Box>
                                <Controller
                                    name="days_of_week"
                                    control={form.control}
                                    render={({ field }) => (
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            {DAYS_OF_WEEK.map((day) => (
                                                <FormControlLabel
                                                    key={day.value}
                                                    control={
                                                        <Checkbox
                                                            checked={field.value.includes(day.value)}
                                                            onChange={(e) => {
                                                                const newValue = e.target.checked
                                                                    ? [...field.value, day.value]
                                                                    : field.value.filter((d) => d !== day.value);
                                                                field.onChange(newValue);
                                                            }}
                                                        />
                                                    }
                                                    label={day.label}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                />
                            </Box>
                        )}

                        {isEditMode && (
                            <Controller
                                name="status"
                                control={form.control}
                                render={({ field }) => (
                                    <TextField {...field} select label="Estado" fullWidth>
                                        <MenuItem value="scheduled">Agendado</MenuItem>
                                        <MenuItem value="created">Criado</MenuItem>
                                    </TextField>
                                )}
                            />
                        )}

                        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button color="secondary" onClick={onClose} disabled={isLoading || isUpdating}>
                                Cancelar
                            </Button>
                            <Button type="submit" variant="contained" disabled={isLoading || isUpdating}>
                                {isEditMode ? "Atualizar" : "Criar"}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </form>
            <ScheduleConflictDialog
                isOpen={isConflictDialogOpen}
                conflictCount={conflictCount}
                onConfirm={handleConfirmConflict}
                onCancel={handleCancelConflict}
            />
        </Drawer>
    );
};
